// The offer workshop (proposed steps 2.30, 2.31; docs/specs/OFFER_WORKSHOP.md): the shapes the offer-workshop Edge
// Function returns, the call to it, and the small pure helpers the screen uses. The interview state lives on the
// server (offer_workshops); this only sends one action at a time with the person's token.
import { env, functionUrl } from "./env";
import { httpFetch } from "./http";
import type { Json, JsonObject } from "./types";

/** The six questions, in order. The same wording as supabase/functions/offer-workshop/lib/workshop.ts. */
export const QUESTIONS = [
  { key: "sell", text: "What do you sell, and what is the first job a new customer usually buys from you?", short: "What you sell" },
  { key: "customers", text: "Name your three best customers and why they bought from you. No customers yet? Describe the customers you most want and why they would buy. Paste website addresses if you have them.", short: "Best customers" },
  { key: "before", text: "What was going wrong for them before they found you (or for the customers you want), and what was it costing them?", short: "Before you" },
  { key: "proof", text: "What proof do you have? Numbers, results, accreditations, customers we may name. Just starting? Your experience, a pilot or why you're the right people counts too.", short: "Proof" },
  { key: "objections", text: "When people say no, what do they usually say, and how do you answer? Not heard many yet? What do you expect them to say?", short: "Objections" },
  { key: "smallest_yes", text: "What do you ask a stranger for first? Something quick, free or cheap.", short: "What you ask for first" },
] as const;
export type QuestionKey = typeof QUESTIONS[number]["key"];

export const CRITERIA: { key: string; label: string; ask: string }[] = [
  { key: "specific", label: "Specific", ask: "Is it clear who it is for and what they get?" },
  { key: "about_reader", label: "About the reader", ask: "Is it about the reader's problem, not the seller?" },
  { key: "credible_proof", label: "Credible proof", ask: "Is there proof a stranger would believe?" },
  { key: "one_small_ask", label: "One small ask", ask: "Is there one ask, and is it small?" },
  { key: "no_jargon", label: "No jargon", ask: "Would a busy owner understand every word?" },
];

export interface OfferCard {
  for_whom: string;
  problem_in_their_words: string;
  what_we_offer: string;
  proof: string[];
  smallest_yes: string;
  objections: { objection: string; answer: string }[];
  words_to_avoid: string[];
}
export interface Angle {
  key: string;
  label: string;
  line: string;
  keep?: boolean;
}
export interface ColdTest {
  score: number;
  opening_line?: string;
  rubric: { criterion: string; score: number; note?: string }[];
  reactions: { persona: string; reaction: string; would_reply: boolean; organisation_id?: string }[];
  tested_at?: string;
}
export type WorkshopStatus = "interview" | "answered" | "drafted" | "tested" | "proposed" | "abandoned";
export interface Workshop {
  id: string;
  status: WorkshopStatus;
  answers: Partial<Record<QuestionKey, { text: string; at?: string }>>;
  card: OfferCard | null;
  card_sources: { label: string; url?: string; untrusted?: boolean }[];
  angles: Angle[];
  cold_test: ColdTest | null;
  config_version: number | null;
  /** Guided set-up: the play this workshop builds (offer_workshops.play_id), or null for the playbook's own offer. */
  play_id?: string | null;
  created_at?: string;
  updated_at?: string;
}
/** 2.43 A lead offer the business's signal packs suggest for the smallest yes (with `get` only). */
export interface SuggestedOffer {
  key: string;
  label: string;
  line: string;
  pack: string;
}
export interface WorkshopView {
  workshop: Workshop | null;
  next_question: { key: QuestionKey; text: string } | null;
  config_version?: number;
  suggested_offers?: SuggestedOffer[];
}

export type WorkshopRequest =
  | { action: "get"; play?: string }
  | { action: "start"; restart?: boolean; play?: string }
  | { action: "answer"; workshop: string; key: QuestionKey; answer: string }
  | { action: "draft" | "test"; workshop: string }
  | { action: "edit_card"; workshop: string; card: OfferCard }
  | { action: "propose"; workshop: string; keep: string[] };

export class WorkshopError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/** One action on the offer-workshop Edge Function. */
export async function callWorkshop(opts: { token: string; body: WorkshopRequest; url?: string; anonKey?: string; fetchImpl?: typeof fetch }): Promise<WorkshopView> {
  const f = opts.fetchImpl ?? httpFetch;
  let res: Response;
  try {
    res = await f(opts.url ?? functionUrl("offer-workshop"), {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${opts.token}`, apikey: opts.anonKey ?? env.supabaseAnonKey },
      body: JSON.stringify(opts.body),
    });
  } catch {
    throw new WorkshopError("Can't reach the server. Check your connection and try again.", 0);
  }
  const body = (await res.json().catch(() => ({}))) as Partial<WorkshopView> & { error?: string };
  if (res.status === 404 && !body.error) throw new WorkshopError("The offer workshop isn't switched on yet. Try again later, or ask us for help.", 404);
  if (res.status === 401) throw new WorkshopError("Your session has ended. Sign in again.", 401);
  if (!res.ok || !("workshop" in body)) throw new WorkshopError(body.error ?? "Something went wrong. Try again in a moment.", res.status);
  return body as WorkshopView;
}

// ---------------------------------------------------------------------------- helpers for the screen
/** How far through the interview: answered of six. */
export function progress(w: Workshop | null): { answered: number; total: number } {
  return { answered: w ? QUESTIONS.filter((q) => w.answers?.[q.key]?.text?.trim()).length : 0, total: QUESTIONS.length };
}

/** The chat so far: each question asked and the answer given, in order, up to the first unanswered one. */
export function transcript(w: Workshop | null): { key: QuestionKey; question: string; answer: string | null }[] {
  const out: { key: QuestionKey; question: string; answer: string | null }[] = [];
  for (const q of QUESTIONS) {
    const a = w?.answers?.[q.key]?.text?.trim() || null;
    out.push({ key: q.key, question: q.text, answer: a });
    if (!a) break;
  }
  return out;
}

/** Where the screen is: the interview, the card, or done. */
export function stage(w: Workshop | null): "none" | "interview" | "draft" | "card" | "done" {
  if (!w || w.status === "abandoned") return "none";
  if (w.status === "interview") return "interview";
  if (w.status === "answered") return "draft";
  if (w.status === "proposed") return "done";
  return "card";
}

/** A score out of ten in words. */
export function scoreWords(score: number): string {
  if (score >= 9) return "Ready to send";
  if (score >= 7) return "Nearly there";
  if (score >= 5) return "Needs work";
  return "Not ready";
}

/** The keys of the angles a person has kept (all kept by default). */
export function keptKeys(angles: Angle[], toggles: Record<string, boolean>): string[] {
  return angles.filter((a) => toggles[a.key] ?? a.keep ?? true).map((a) => a.key);
}

/** Lines in a text box to a clean list (blank lines dropped). */
export function linesToList(text: string, max: number): string[] {
  return text.split("\n").map((s) => s.trim()).filter(Boolean).slice(0, max);
}

/** A suggested offer tapped into the answer box: the whole answer when it is empty, else on a new line. */
export function withSuggestion(answer: string, line: string): string {
  const a = answer.replace(/\s+$/, "");
  return a ? `${a}\n${line}` : line;
}

/** "Objection | answer" lines to objections; a line without a bar is dropped. */
export function linesToObjections(text: string): OfferCard["objections"] {
  return linesToList(text, 8)
    .map((l) => {
      const i = l.indexOf("|");
      return i < 0 ? null : { objection: l.slice(0, i).trim(), answer: l.slice(i + 1).trim() };
    })
    .filter((o): o is { objection: string; answer: string } => !!o && !!o.objection && !!o.answer);
}

export function objectionsToLines(o: OfferCard["objections"]): string {
  return o.map((x) => `${x.objection} | ${x.answer}`).join("\n");
}

/** What is missing before a card can be saved, in plain words; empty when it is fine. */
export function cardGaps(c: OfferCard): string[] {
  const out: string[] = [];
  if (!c.for_whom.trim()) out.push("who it is for");
  if (!c.problem_in_their_words.trim()) out.push("the problem in their words");
  if (!c.what_we_offer.trim()) out.push("what you offer");
  if (!c.smallest_yes.trim()) out.push("what you're asking for first");
  return out;
}

// ---------------------------------------------------------------------------- reading it back from settings
const isObj = (v: unknown): v is JsonObject => typeof v === "object" && v !== null && !Array.isArray(v);
const s = (v: Json | undefined): string => (typeof v === "string" ? v.trim() : "");

/** The offer card, angles and cold-test score in a settings version, for the playbook's offer section. Null parts are absent. */
export function offerFromConfig(config: JsonObject | null | undefined): { card: OfferCard | null; angles: Angle[]; score: number | null } {
  const offer = isObj(config?.offer) ? (config!.offer as JsonObject) : {};
  const c = isObj(offer.card) ? (offer.card as JsonObject) : null;
  const list = (v: Json | undefined) => (Array.isArray(v) ? v.map((x) => s(x)).filter(Boolean) : []);
  const card: OfferCard | null = c && s(c.what_we_offer)
    ? {
        for_whom: s(c.for_whom),
        problem_in_their_words: s(c.problem_in_their_words),
        what_we_offer: s(c.what_we_offer),
        proof: list(c.proof),
        smallest_yes: s(c.smallest_yes),
        objections: (Array.isArray(c.objections) ? c.objections : [])
          .filter(isObj)
          .map((o) => ({ objection: s(o.objection), answer: s(o.answer) }))
          .filter((o) => o.objection && o.answer),
        words_to_avoid: list(c.words_to_avoid),
      }
    : null;
  const angles = (Array.isArray(offer.angles) ? offer.angles : [])
    .filter(isObj)
    .map((a) => ({ key: s(a.key), label: s(a.label), line: s(a.line) }))
    .filter((a) => a.key && a.line);
  const t = isObj(offer.cold_test) ? (offer.cold_test as JsonObject) : null;
  const score = t && typeof t.score === "number" ? t.score : null;
  return { card, angles, score };
}
